import { Link, createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { SectionCard } from '@/components/console/section-card'
import { DetailSkeleton, ErrorState } from '@/components/console/states'
import { PageContainer } from '@/components/layout/page-container'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiErrorMessage, isNotFound, useOrderDetail } from '@/features/orders/api'
import { OrderItemsTable } from '@/features/orders/components/order-items-table'
import { OrderHeaderCard, OrderOverviewCard } from '@/features/orders/components/order-overview'
import { OrderPaymentPlanCard } from '@/features/orders/components/order-payment-plan'
import { OrderStatusTimeline } from '@/features/orders/components/order-status-timeline'
import { OrderTravelersTable } from '@/features/orders/components/order-travelers-table'
import {
  ORDER_DETAIL_SEARCH_DEFAULTS,
  orderDetailSearchSchema,
  type OrderDetailTab,
} from '@/features/orders/search-params'

export const Route = createFileRoute('/_authed/orders/$orderId')({
  // tab 也是有意义状态:冷加载 /orders/ORD-xxx?tab=payment 直接落在付款计划页。
  // 默认 tab 由 stripSearchParams 摘掉(A4 全站统一),裸地址保持干净。
  validateSearch: orderDetailSearchSchema,
  search: {
    middlewares: [stripSearchParams(ORDER_DETAIL_SEARCH_DEFAULTS)],
  },
  component: OrderDetailPage,
})

function OrderDetailPage() {
  const { orderId } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const query = useOrderDetail(orderId)

  const order = query.data

  return (
    <PageContainer
      title={`订单 ${orderId}`}
      breadcrumbs={[{ label: '订单管理', to: '/orders' }, { label: orderId }]}
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link to="/orders">
            <ArrowLeft className="size-3.5" />
            返回列表
          </Link>
        </Button>
      }
    >
      {query.isLoading ? (
        <DetailSkeleton fields={14} />
      ) : query.isError || !order ? (
        <ErrorState
          title={isNotFound(query.error) ? `订单 ${orderId} 不存在或已被移除` : '订单详情加载失败'}
          message={isNotFound(query.error) ? '资源号可能有误,或该订单已被清理。' : apiErrorMessage(query.error)}
          onRetry={() => {
            void query.refetch()
          }}
          action={
            <Button variant="outline" size="sm" asChild>
              <Link to="/orders">回到订单列表</Link>
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          <OrderHeaderCard order={order} />
          <OrderOverviewCard order={order} />

          <Tabs
            value={tab}
            onValueChange={(next) => {
              void navigate({ search: { tab: next as OrderDetailTab }, replace: true })
            }}
          >
            <TabsList variant="line" className="h-9 border-b border-console-border">
              <TabsTrigger value="items">行项({order.items.length})</TabsTrigger>
              <TabsTrigger value="payment">付款计划</TabsTrigger>
              <TabsTrigger value="history">状态史({order.status_history.length})</TabsTrigger>
              <TabsTrigger value="travelers">出行人({order.travelers.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="items">
              <SectionCard bodyClassName="p-0">
                <OrderItemsTable items={order.items} />
              </SectionCard>
            </TabsContent>

            <TabsContent value="payment">
              <SectionCard bodyClassName="p-0">
                <OrderPaymentPlanCard order={order} />
              </SectionCard>
            </TabsContent>

            <TabsContent value="history">
              <SectionCard bodyClassName="p-0">
                <OrderStatusTimeline history={order.status_history} />
              </SectionCard>
            </TabsContent>

            <TabsContent value="travelers">
              <SectionCard bodyClassName="p-0">
                <OrderTravelersTable travelers={order.travelers} />
              </SectionCard>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </PageContainer>
  )
}
